// Handlebars template for the customer-facing "Quote illustration" PDF generated
// on the confirmation page. Reproduces the legacy CETA bridge-quote-system layout:
//   1. Statement Of Price - Cover Details (selected scheme premium + cover limits/excesses)
//   2. Policy endorsements & exclusions (scheme endorsement text)
//   3. Statement Of Fact (every journey answer, grouped)
//
// Rendered through usePdfGenerator (jsPDF.html + html2canvas) via the /pdf.html
// background tab. The generator renders the DOM at a 1200px design width and
// scales it (~0.375) onto A4, so font sizes/padding here are ~2x their apparent
// print size. All styling lives in a single <style> block (hoisted into <head>
// before capture); the logo is an inline SVG data URI (rasterised to PNG by
// rasterizeSvgImages).
//
// Rules (horizontal lines) are attached to their heading via a bottom border
// rather than a standalone <hr>, so autopaging can never orphan a rule onto the
// wrong page. Sub-labels (e.g. "(Including IPT)") sit on their own tight line
// beneath the label so they can never collide with the value.
export const quoteIllustrationTemplate = `
<style>
  .qi-container {
    font-family: 'Helvetica', 'Arial', sans-serif;
    padding: 70px 64px;
    width: 1040px;
    max-width: 100%;
    line-height: 1.35;
    font-size: 19px;
    color: #1a1a1a;
    box-sizing: border-box;
    overflow-wrap: break-word;
    word-break: break-word;
  }

  .qi-logo {
    margin-bottom: 40px;
  }

  .qi-logo img {
    width: 100px;
    height: auto;
    display: block;
  }

  .qi-title-row {
    display: flex;
    align-items: center;
    gap: 22px;
    margin-bottom: 40px;
  }

  .qi-title {
    font-size: 42px;
    font-weight: bold;
    color: #1a1a1a;
    margin: 0;
  }

  .qi-reference {
    border: 1px solid #cfcfcf;
    border-radius: 10px;
    padding: 10px 20px;
    font-size: 28px;
    font-weight: bold;
    color: #1a1a1a;
    white-space: nowrap;
  }

  .qi-section {
    margin-bottom: 42px;
  }

  .qi-section-title {
    font-size: 30px;
    font-weight: bold;
    color: #1a1a1a;
    margin: 0 0 20px 0;
  }

  .qi-subtitle {
    font-size: 24px;
    font-weight: bold;
    color: #1a1a1a;
    margin: 0 0 20px 0;
  }

  /* A rule glued to the heading above it. */
  .qi-ruled {
    padding-bottom: 16px;
    border-bottom: 1px solid #d9d9d9;
    page-break-after: avoid;
  }

  /* Statement of Price - two label/value pairs per row */
  .qi-price-grid {
    display: flex;
    flex-wrap: wrap;
  }

  .qi-price-cell {
    width: 50%;
    box-sizing: border-box;
    padding: 0 22px 26px 0;
    page-break-inside: avoid;
    overflow-wrap: break-word;
    word-break: break-word;
  }

  .qi-label {
    font-weight: bold;
    color: #1a1a1a;
    margin: 0;
    line-height: 1.25;
  }

  .qi-sublabel {
    display: block;
    font-weight: normal;
    color: #555;
    font-size: 15px;
    line-height: 1.25;
    margin: 2px 0 0 0;
  }

  .qi-value {
    color: #1a1a1a;
    margin: 6px 0 0 0;
  }

  /* Qualifier under a value, e.g. "Max £2,500 pm" on the rental cover row. */
  .qi-subvalue {
    color: #0e6e7d;
    font-size: 15px;
    line-height: 1.25;
    margin: 2px 0 0 0;
  }

  /* Statement of Fact - single column, label left / value right */
  .qi-sof-row {
    display: flex;
    align-items: flex-start;
    padding: 9px 0;
    page-break-inside: avoid;
  }

  .qi-sof-label {
    flex: 0 0 58%;
    max-width: 58%;
    font-weight: bold;
    color: #1a1a1a;
    padding-right: 24px;
    box-sizing: border-box;
    overflow-wrap: break-word;
    word-break: break-word;
  }

  .qi-sof-value {
    flex: 1;
    color: #1a1a1a;
    overflow-wrap: break-word;
    word-break: break-word;
  }

  /* Endorsements */
  .qi-endorsement {
    margin-bottom: 30px;
    page-break-inside: avoid;
  }

  .qi-endorsement-summary {
    font-size: 22px;
    font-weight: bold;
    color: #1a1a1a;
    margin: 0 0 10px 0;
  }

  .qi-endorsement-content {
    color: #1a1a1a;
  }

  /* Fixed px line-height (not unitless) + explicit baseline: html2canvas
     mis-computes the baseline of inline bold runs inside wrapped text when the
     line-height is unitless, dropping them onto the wrong line. */
  .qi-endorsement-content,
  .qi-endorsement-content p,
  .qi-endorsement-content li {
    line-height: 28px;
  }

  .qi-endorsement-content b,
  .qi-endorsement-content strong,
  .qi-endorsement-content span {
    font-weight: inherit;
    line-height: inherit;
    vertical-align: baseline;
  }

  .qi-endorsement-content ol,
  .qi-endorsement-content ul {
    margin: 10px 0;
    padding-left: 30px;
  }

  .qi-endorsement-content li {
    margin: 6px 0;
  }

  .qi-empty {
    color: #666;
  }

  .qi-sof-section {
    margin-top: 20px;
  }

  .qi-section-note {
    padding: 1rem;
  }

  .qi-section-note-alert {
      position: relative;
      padding: 0.75rem 1.25rem;
      margin-bottom: 0;
      border: 1px solid #ffeeba;
      border-radius: 0.25rem;
      color: #856404;
      background-color: #fff3cd;
      font-weight: bold;
  }
</style>

<div class="qi-container">
  <div class="qi-logo">
    <img src="{{logo}}" alt="{{brand}}" />
  </div>

  <div class="qi-title-row">
    <h1 class="qi-title">Quote illustration</h1>
    {{#if reference}}<span class="qi-reference">{{reference}}</span>{{/if}}
  </div>

  <div class="qi-section">
    {{#if productName}}
      <h2 class="qi-section-title">Statement Of Price - Cover Details</h2>
      <h3 class="qi-subtitle qi-ruled">{{productName}}</h3>
    {{else}}
      <h2 class="qi-section-title qi-ruled">Statement Of Price - Cover Details</h2>
    {{/if}}
    <div class="qi-price-grid">
      {{#each priceItems}}
        <div class="qi-price-cell">
          <p class="qi-label">{{label}}</p>
          {{#if sublabel}}<p class="qi-sublabel">{{sublabel}}</p>{{/if}}
          <p class="qi-value">{{value}}</p>
          {{#if subvalue}}<p class="qi-subvalue">{{subvalue}}</p>{{/if}}
        </div>
      {{/each}}
    </div>
  </div>

  <div class="qi-section">
    <h2 class="qi-section-title qi-ruled">Policy endorsements &amp; exclusions</h2>
    {{#if endorsements.length}}
      {{#each endorsements}}
        <div class="qi-endorsement">
          {{#if summary}}<p class="qi-endorsement-summary">{{summary}}</p>{{/if}}
          <div class="qi-endorsement-content">{{{content}}}</div>
        </div>
      {{/each}}
    {{else}}
      <p class="qi-empty">No endorsements apply to this policy.</p>
    {{/if}}
  </div>

  <div class="qi-section">
    <h2 class="qi-section-title">Statement Of Fact</h2>
    {{#each sofSections}}
      <div class="qi-sof-section">
        <h3 class="qi-subtitle qi-ruled">{{title}}</h3>
        {{#each items}}
          <div class="qi-sof-row">
            <div class="qi-sof-label">{{label}}</div>
            <div class="qi-sof-value">{{value}}</div>
          </div>
        {{/each}}
        {{#if notePDF}}<div class="qi-section-note"><div class="qi-section-note-alert">{{notePDF}}</div></div>{{/if}}
      </div>
    {{/each}}
  </div>
</div>
`;

// Inline CETA logo (gold horseshoe mark + "ceta" wordmark) as an SVG data URI.
// Self-contained so the background-tab renderer needs no network access and
// html2canvas won't taint the canvas with a cross-origin image. The mark is an
// even-thickness stroked ring with a gap (round caps) approximating the brand
// horseshoe. Swap this for the official brand asset (as a data URI) if a
// pixel-exact logo is required.
export const CETA_LOGO_DATA_URI =
  'data:image/svg+xml;base64,' +
  btoa(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 260 150">
    <circle cx="72" cy="60" r="42" fill="none" stroke="#F9B000" stroke-width="17"
      stroke-linecap="round" stroke-dasharray="215 49" transform="rotate(128 72 60)"/>
    <text x="72" y="142" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
      font-size="44" font-weight="bold" fill="#0e6e7d" letter-spacing="1">ceta</text>
  </svg>`);
